import { useMemo, useCallback, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDrHistoryLatest, useDrHistoryRange } from './queries/drHistoryQueries';
import { useKpxSmpToday } from './queries/kpxQueries';
import { useWeather, useWeatherToday } from './queries/weatherQueries';
import { useSigunguEupmyeondongStats, useDRResourcesAll, useDRResource, useRegionClusterStatPack } from './queries/drResourceQueries';
import { usePredictionCblPackData, usePowerMeterDemandPackData, useLivePowerMeterDemandPackData, useLiveCblDataPackData } from './queries/packStatQueries';
import { useSetUpList } from './queries/setUpQueries';
import dayjs from 'dayjs';

/**
 * Dashboard용 실시간 데이터 훅
 * 전력 수요, DR 발령 이력, 충전소, DR 자원, 충전기 데이터를 통합 제공
 * @param {Object} regionInfo - 지역 정보 { regionCode, sigungu, sido, eupmyeondong }
 * @param {string} drResourceId - DR 자원 ID
 */
export const useDashboardRealtimeData = (regionInfo = null, drResourceId = null) => {
  const queryClient = useQueryClient();
  const hasErrorRef = useRef(false);

  const { data: setUpData } = useSetUpList();
  const defaultCblModuleName = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_NAME')?.Value;
  const defaultCblModuleVersion = setUpData?.Items?.find(item => item.Group === 'DefaultCBL' && item.Key === 'DEFAULT_MODULE_VERSION')?.Value;
  const defaultChargingDemandPredictionModuleName = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_NAME')?.Value;
  const defaultChargingDemandPredictionModuleVersion = setUpData?.Items?.find(item => item.Group === 'ChargingDemandAi' && item.Key === 'CHARGING_DEMAND_AI_VERSION')?.Value;
  // regionInfo에서 쿼리 파라미터 구성
  // drResourceId가 있으면 regionInfo를 무시하고 빈 객체 반환 (DR 자원 기반 조회 우선)
  // 읍면동까지 선택했는지 확인
  const hasEupmyeondong = useMemo(() => {
    if (!regionInfo) return false;
    
    // drResourceId가 있을 때는 eupmyeondong만 확인 (regionCode는 null일 수 있음)
    if (drResourceId) {
      return !!(regionInfo.eupmyeondong && regionInfo.eupmyeondong.trim() !== '');
    }
    
    // drResourceId가 없을 때는 eupmyeondong과 regionCode가 모두 있어야 함 (빈 문자열도 체크)
    return !!(regionInfo.eupmyeondong && regionInfo.regionCode && 
              regionInfo.eupmyeondong.trim() !== '' && 
              regionInfo.regionCode !== null && 
              regionInfo.regionCode !== undefined);
  }, [regionInfo, drResourceId]);

  // 읍면동까지 선택해야만 데이터 조회
  const drResourceQueryParams = useMemo(() => {
    // drResourceId가 있으면 지역 기반 조회를 비활성화
    if (drResourceId) return {};
    if (!hasEupmyeondong) return {};
    if (!regionInfo || !regionInfo.regionCode) return {};
    
    const params = {};
    
    // 읍면동이 있으면 RegionCode 사용 (8자리)
    params.RegionCode = regionInfo.regionCode.toString().padEnd(10, '0');
    
    return params;
  }, [regionInfo, drResourceId, hasEupmyeondong]);

  // DR 자원 단위 조회 (항상 호출, drResourceId가 없으면 비활성화)
  const {
    data: drResourceDataById,
    isLoading: drResourceLoadingById,
    error: drResourceErrorById,
    refetch: refetchDrResourceById
  } = useDRResource(drResourceId, { enabled: !!drResourceId });

  // 지역 단위 DR 자원 전체 조회 (읍면동까지 선택했을 때만)
  // enabled 조건을 더 엄격하게 체크
  const isDrResourceQueryEnabled = useMemo(() => {
    if (drResourceId) return false;
    if (!hasEupmyeondong) return false;
    if (!drResourceQueryParams || !drResourceQueryParams.RegionCode) return false;
    return true;
  }, [drResourceId, hasEupmyeondong, drResourceQueryParams]);

  const {
    data: drResourceDataByRegion,
    isLoading: drResourceLoadingByRegion,
    error: drResourceErrorByRegion,
    refetch: refetchDrResourceByRegion
  } = useDRResourcesAll(
    drResourceQueryParams,
    { 
      enabled: isDrResourceQueryEnabled
    }
  );

  // 실제 사용할 데이터 선택
  const drResourceData = drResourceId 
    ? (drResourceDataById ? {Items: [drResourceDataById]} : {Items: []})
    : drResourceDataByRegion;
  const drResourceLoading = drResourceId ? drResourceLoadingById : drResourceLoadingByRegion;
  const drResourceError = drResourceId ? drResourceErrorById : drResourceErrorByRegion;
  const refetchDrResource = drResourceId ? refetchDrResourceById : refetchDrResourceByRegion;

  // DR 자원에서 충전소 목록 추출
  const stationSysIdList = useMemo(() => {
    if (!drResourceData?.Items || !Array.isArray(drResourceData.Items)) {
      return [];
    }
    
    const stationIds = new Set();
    drResourceData.Items.forEach(resource => {
      if (resource.Stations && Array.isArray(resource.Stations)) {
        resource.Stations.forEach(station => {
          if (station.StationSysId) {
            stationIds.add(station.StationSysId);
          }
        });
      }
    });
    
    const result = Array.from(stationIds);
    return result;
  }, [drResourceData]);

  // 데이터 조회 기준일
  const targetDate = dayjs().format('YYYY-MM-DD');
  
  // 00시 예측 CBL 조회 (읍면동까지 선택했을 때만)
  const {
    data: predictionCblData,
    isLoading: predictionCblLoading,
    error: predictionCblError,
    refetch: refetchPredictionCbl
  } = usePredictionCblPackData(
    stationSysIdList, 
    targetDate, 
    defaultCblModuleName, 
    defaultCblModuleVersion,
    { enabled: hasEupmyeondong && stationSysIdList.length > 0 }
  );
  
  // 실시간 예측 CBL (읍면동까지 선택했을 때만)
  const {
    data: liveCblData,
    isLoading: liveCblLoading,
    error: liveCblError,
    refetch: refetchLiveCbl
  } = useLiveCblDataPackData(
    stationSysIdList, 
    targetDate, 
    defaultCblModuleName, 
    defaultCblModuleVersion,
    { enabled: hasEupmyeondong && stationSysIdList.length > 0 }
  );


  // 어제 전력 수요 조회 (읍면동까지 선택했을 때만)
  const {
    data: powerMeterDemandData,
    isLoading: powerMeterDemandLoading,
    error: powerMeterDemandError,
    refetch: refetchPowerMeterDemand
  } = usePowerMeterDemandPackData(
    stationSysIdList, 
    dayjs(targetDate).subtract(1, 'day').format('YYYY-MM-DD'),
    { enabled: hasEupmyeondong && stationSysIdList.length > 0 }
  );
  

  // 실시간 전력 수요 조회 (읍면동까지 선택했을 때만)
  const {
    data: livePowerMeterDemandData,
    isLoading: livePowerMeterDemandLoading,
    error: livePowerMeterDemandError,
    refetch: refetchLivePowerMeterDemand
  } = useLivePowerMeterDemandPackData(
    stationSysIdList, 
    targetDate,
    { enabled: hasEupmyeondong && stationSysIdList.length > 0 }
  );
  
  
  

  // 지역별 지역 클러스터 정보 - 제주시
  const {
    data: jejuSiData,
    isLoading: jejuSiLoading,
    error: jejuSiError,
    refetch: refetchJejuSi
  } = useSigunguEupmyeondongStats('제주시', { enabled: hasEupmyeondong });

  // 지역별 지역 클러스터 정보 - 서귀포시
  const {
    data: seoguipoSiData,
    isLoading: seoguipoSiLoading,
    error: seoguipoSiError,
    refetch: refetchSeoguipoSi
  } = useSigunguEupmyeondongStats('서귀포시', { enabled: hasEupmyeondong });

  // 날씨 데이터 (6시간 예보) - 읍면동 단위일 때만 호출
  // drResourceId가 있으면 날씨 데이터를 비활성화 (DR 자원 기반 조회 우선)
  const weatherRegionCode = drResourceId ? null : (regionInfo?.regionCode || null);
  // 읍면동 단위인지 확인 (eupmyeondong이 있고 regionCode가 있을 때, drResourceId가 없을 때만)
  const isEupmyeondongLevel = !!(drResourceId ? false : (regionInfo?.eupmyeondong && weatherRegionCode));
  const isWeatherLevel = !!(drResourceId ? false : regionInfo?.weather);
  
  const {
    data: weatherForecastData,
    isLoading: weatherForecastLoading,
    error: weatherForecastError,
    refetch: refetchWeatherForecast
  } = useWeather(weatherRegionCode, { enabled: isEupmyeondongLevel || isWeatherLevel });

  // 날씨 데이터 (금일 24시간 누적) - 읍면동 단위일 때만 호출
  const targetDateStr = dayjs().format('YYYY-MM-DD');
  const {
    data: weatherTodayData,
    isLoading: weatherTodayLoading,
    error: weatherTodayError,
    refetch: refetchWeatherToday
  } = useWeatherToday(weatherRegionCode, targetDateStr, { enabled: isEupmyeondongLevel || isWeatherLevel });

  // DR 발령 이력 데이터
  const {
    data: drHistoryLatestData,
    isLoading: drHistoryLatestLoading,
    error: drHistoryLatestError,
    refetch: refetchDrHistoryLatest
  } = useDrHistoryLatest();

  // DR 발령 이력 데이터 (범위)
  const {
    data: drHistoryRangeData,
    isLoading: drHistoryRangeLoading,
    error: drHistoryRangeError,
    refetch: refetchDrHistoryRange
  } = useDrHistoryRange({
    startDate: dayjs().subtract(3, 'month').format('YYYYMMDD'),
    endDate: dayjs().format('YYYYMMDD')
  });

  // SMP 데이터
  const {
    data: kpxSmpRangeData,
    isLoading: kpxSmpRangeLoading,
    error: kpxSmpRangeError,
    refetch: refetchKpxSmpRange
  } = useKpxSmpToday(
    {
      startDate: dayjs().format('YYYY-MM-DD'),
      areaCode: 9
    }
  );

  // 지역별 지역 클러스터 정보 - 제주시 (읍면동까지 선택했을 때만)
  const {
    data: regionClusterStatData,
    isLoading: regionClusterStatLoading,
    error: regionClusterStatError,
    refetch: refetchRegionClusterStat
  } = useRegionClusterStatPack({
    targetDate,
    StationSysIdList: stationSysIdList
  }, { enabled: hasEupmyeondong && stationSysIdList.length > 0 });

  // 최근 DR 발령일 전력 수요 조회 (읍면동까지 선택했을 때만)
  const {
    data: drEventPowerMeterDemandData,
    isLoading: drEventPowerMeterDemandLoading,
    error: drEventPowerMeterDemandError,
    refetch: refetchDrEventPowerMeterDemand
  } = usePowerMeterDemandPackData(
    stationSysIdList, 
    dayjs(drHistoryLatestData?.date).format('YYYY-MM-DD'),
    { enabled: hasEupmyeondong && stationSysIdList.length > 0 }
  );

  // 통합 로딩 상태
  const isLoading = drHistoryLatestLoading || drHistoryRangeLoading || kpxSmpRangeLoading || 
    ((isEupmyeondongLevel || isWeatherLevel) && (weatherForecastLoading || weatherTodayLoading)) || // 읍면동 레벨일 때만 날씨 로딩 포함
    (stationSysIdList.length > 0 && (predictionCblLoading || powerMeterDemandLoading || livePowerMeterDemandLoading || drEventPowerMeterDemandLoading));

  // 통합 에러 상태 (모든 에러 포함) - !!를 사용하여 boolean으로 변환
  const hasError = !!(
    drResourceError || // DR 자원 에러
    jejuSiError || // 제주시 에러
    seoguipoSiError || // 서귀포시 에러
    drHistoryLatestError || 
    drHistoryRangeError || 
    kpxSmpRangeError || 
    regionClusterStatError || // 지역 클러스터 통계 에러
    ((isEupmyeondongLevel || isWeatherLevel) && (weatherForecastError || weatherTodayError)) || // 읍면동 레벨일 때만 날씨 에러 포함
    (stationSysIdList.length > 0 && (predictionCblError || powerMeterDemandError || livePowerMeterDemandError || drEventPowerMeterDemandError))
  );

  // 개별 에러 상태 추적 (어떤 API가 에러인지 확인)
  const errorStates = useMemo(() => ({
    drResourceError: !!drResourceError,
    jejuSiError: !!jejuSiError,
    seoguipoSiError: !!seoguipoSiError,
    drHistoryLatestError: !!drHistoryLatestError,
    drHistoryRangeError: !!drHistoryRangeError,
    kpxSmpRangeError: !!kpxSmpRangeError,
    regionClusterStatError: !!regionClusterStatError,
    weatherForecastError: !!(isEupmyeondongLevel || isWeatherLevel) && !!weatherForecastError,
    weatherTodayError: !!(isEupmyeondongLevel || isWeatherLevel) && !!weatherTodayError,
    predictionCblError: stationSysIdList.length > 0 && !!predictionCblError,
    powerMeterDemandError: stationSysIdList.length > 0 && !!powerMeterDemandError,
    livePowerMeterDemandError: stationSysIdList.length > 0 && !!livePowerMeterDemandError,
    drEventPowerMeterDemandError: stationSysIdList.length > 0 && !!drEventPowerMeterDemandError,
  }), [
    drResourceError,
    jejuSiError,
    seoguipoSiError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    weatherForecastError,
    weatherTodayError,
    predictionCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drEventPowerMeterDemandError,
    isEupmyeondongLevel,
    isWeatherLevel,
    stationSysIdList.length,
  ]);

  // 에러 발생 시 선택적 처리 (과도한 취소 방지)
  // 주의: 모든 쿼리를 취소하지 않고, 에러가 발생한 쿼리만 제한적으로 처리
  useEffect(() => {
    // 에러가 있는 쿼리만 retry 제한 (완전 비활성화 대신 제한)
    if (errorStates.drResourceError) {
      queryClient.setQueryDefaults(['drResource'], { 
        retry: 1, // 1회만 재시도
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.drHistoryLatestError || errorStates.drHistoryRangeError) {
      queryClient.setQueryDefaults(['drHistory'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.kpxSmpRangeError) {
      queryClient.setQueryDefaults(['kpx'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.weatherForecastError || errorStates.weatherTodayError) {
      queryClient.setQueryDefaults(['weather'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.predictionCblError || errorStates.powerMeterDemandError || 
        errorStates.livePowerMeterDemandError || errorStates.drEventPowerMeterDemandError) {
      queryClient.setQueryDefaults(['packStat'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }
    if (errorStates.regionClusterStatError) {
      queryClient.setQueryDefaults(['regionCluster'], { 
        retry: 1,
        refetchOnWindowFocus: false 
      });
    }

    // 에러가 해결된 쿼리는 기본 설정으로 복구
    if (!errorStates.drResourceError) {
      queryClient.setQueryDefaults(['drResource'], {});
    }
    if (!errorStates.drHistoryLatestError && !errorStates.drHistoryRangeError) {
      queryClient.setQueryDefaults(['drHistory'], {});
    }
    if (!errorStates.kpxSmpRangeError) {
      queryClient.setQueryDefaults(['kpx'], {});
    }
    if (!errorStates.weatherForecastError && !errorStates.weatherTodayError) {
      queryClient.setQueryDefaults(['weather'], {});
    }
    if (!errorStates.predictionCblError && !errorStates.powerMeterDemandError && 
        !errorStates.livePowerMeterDemandError && !errorStates.drEventPowerMeterDemandError) {
      queryClient.setQueryDefaults(['packStat'], {});
    }
    if (!errorStates.regionClusterStatError) {
      queryClient.setQueryDefaults(['regionCluster'], {});
    }
  }, [errorStates, queryClient]);

  // refetch 함수들을 ref로 저장하여 안정화
  const refetchFunctionsRef = useRef({
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchJejuSi,
    refetchSeoguipoSi,
    refetchWeatherForecast,
    refetchWeatherToday,
    refetchDrResource,
    refetchPredictionCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrEventPowerMeterDemand,
    refetchRegionClusterStat
  });

  // 에러 상태들을 ref로 저장하여 refetchAll에서 접근 가능하게 함
  const errorsRef = useRef({
    drResourceError,
    jejuSiError,
    seoguipoSiError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    weatherForecastError,
    weatherTodayError,
    predictionCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drEventPowerMeterDemandError
  });

  // refetch 함수들 업데이트
  useEffect(() => {
    refetchFunctionsRef.current = {
      refetchDrHistoryLatest,
      refetchDrHistoryRange,
      refetchKpxSmpRange,
      refetchJejuSi,
      refetchSeoguipoSi,
      refetchWeatherForecast,
      refetchWeatherToday,
      refetchDrResource,
      refetchPredictionCbl,
      refetchPowerMeterDemand,
      refetchLivePowerMeterDemand,
      refetchDrEventPowerMeterDemand,
      refetchRegionClusterStat
    };
  }, [
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchJejuSi,
    refetchSeoguipoSi,
    refetchWeatherForecast,
    refetchWeatherToday,
    refetchDrResource,
    refetchPredictionCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrEventPowerMeterDemand,
    refetchRegionClusterStat
  ]);

  // 에러 상태들 업데이트
  useEffect(() => {
    errorsRef.current = {
      drResourceError,
      jejuSiError,
      seoguipoSiError,
      drHistoryLatestError,
      drHistoryRangeError,
      kpxSmpRangeError,
      regionClusterStatError,
      weatherForecastError,
      weatherTodayError,
      predictionCblError,
      powerMeterDemandError,
      livePowerMeterDemandError,
      drEventPowerMeterDemandError
    };
  }, [
    drResourceError,
    jejuSiError,
    seoguipoSiError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    regionClusterStatError,
    weatherForecastError,
    weatherTodayError,
    predictionCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drEventPowerMeterDemandError
  ]);

  // 실시간 데이터 새로고침 함수 (ref를 사용하여 완전히 안정화)
  const refetchAll = useCallback(async () => {
    const refs = refetchFunctionsRef.current;
    const promises = [];
    
    if (refs.refetchDrHistoryLatest) {
      promises.push(refetchDrHistoryLatest());
    }
    
    if (refs.refetchDrHistoryRange) {
      promises.push(refetchDrHistoryRange());
    }

    if (refs.refetchKpxSmpRange) {
      promises.push(refetchKpxSmpRange());
    }

    if (refs.refetchJejuSi) {
      promises.push(refetchJejuSi());
    }

    if (refs.refetchSeoguipoSi) {
      promises.push(refetchSeoguipoSi());
    }

    // 읍면동 레벨일 때만 날씨 데이터 refetch
    if (isEupmyeondongLevel || isWeatherLevel) {
      if (refs.refetchWeatherForecast) {
        promises.push(refetchWeatherForecast());
      }

      if (refs.refetchWeatherToday) {
        promises.push(refetchWeatherToday());
      }
    }

    if (refs.refetchDrResource) {
      promises.push(refetchDrResource());
    }

    // stationSysIdList가 비어있을 때는 pack 관련 refetch 하지 않음
    if (stationSysIdList.length > 0) {
      if (refs.refetchPredictionCbl) {
        promises.push(refetchPredictionCbl());
      }

      if (refs.refetchPowerMeterDemand) {
        promises.push(refetchPowerMeterDemand());
      }

      if (refs.refetchLivePowerMeterDemand) {
        promises.push(refetchLivePowerMeterDemand());
      }

      if (refs.refetchDrEventPowerMeterDemand) {
        promises.push(refetchDrEventPowerMeterDemand());
      }

      if (refs.refetchRegionClusterStat) {
        promises.push(refetchRegionClusterStat());
      }
    }

    // 모든 refetch 완료 대기 (React Query의 refetch는 에러가 있어도 reject하지 않음)
    const results = await Promise.allSettled(promises);
    
    // 실패한 refetch 확인 (로깅용, throw하지 않음)
    const failedRefetches = results
      .map((result, index) => ({ result, index }))
      .filter(({ result }) => result.status === 'rejected');
    
    if (failedRefetches.length > 0) {
      console.warn(`[useDashboardRealtimeData] ${failedRefetches.length}개의 API refetch 실패 (일부 성공 가능)`);
      failedRefetches.forEach(({ result, index }) => {
        console.warn(`[useDashboardRealtimeData] Refetch ${index} 실패:`, result.reason);
      });
    }
    
  }, [isEupmyeondongLevel, isWeatherLevel, stationSysIdList.length]);

  // 반환 객체를 useMemo로 안정화하여 불필요한 재렌더링 방지
  // 중요한 값들만 의존성 배열에 포함
  // 읍면동까지 선택하지 않았을 때는 pack 관련 데이터를 undefined로 반환 (캐시된 데이터 사용 방지)
  return useMemo(() => ({
    defaultCblModuleName,
    defaultCblModuleVersion,
    jejuSiData,
    seoguipoSiData,
    // 실시간 데이터
    drResourceData,
    stationSysIdList,
    // 읍면동까지 선택하지 않았을 때는 undefined 반환 (React Query 캐시 사용 방지)
    predictionCblData: hasEupmyeondong ? predictionCblData : undefined,
    liveCblData: hasEupmyeondong ? liveCblData : undefined,
    powerMeterDemandData: hasEupmyeondong ? powerMeterDemandData : undefined,
    livePowerMeterDemandData: hasEupmyeondong ? livePowerMeterDemandData : undefined,
    drHistoryLatestData,
    drHistoryRangeData,
    kpxSmpRangeData,
    weatherForecastData,
    weatherTodayData,
    regionClusterStatData: hasEupmyeondong ? regionClusterStatData : undefined,
    drEventPowerMeterDemandData: hasEupmyeondong ? drEventPowerMeterDemandData : undefined,
    // 로딩 상태
    isLoading,
    jejuSiLoading,
    seoguipoSiLoading,
    drResourceLoading,
    predictionCblLoading,
    liveCblLoading,
    powerMeterDemandLoading,
    livePowerMeterDemandLoading,
    drHistoryLatestLoading,
    drHistoryRangeLoading,
    kpxSmpRangeLoading,
    weatherForecastLoading,
    weatherTodayLoading,
    regionClusterStatLoading,
    drEventPowerMeterDemandLoading,
    // 에러 상태
    hasError,
    jejuSiError,
    seoguipoSiError,
    drResourceError,
    predictionCblError,
    liveCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    weatherForecastError,
    weatherTodayError,
    regionClusterStatError,
    drEventPowerMeterDemandError,
    // 새로고침 함수 (refetchAll은 안정화되어 있으므로 포함)
    refetchAll,
    refetchJejuSi,
    refetchSeoguipoSi,
    refetchDrResource,
    refetchPredictionCbl,
    refetchLiveCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchWeatherForecast,
    refetchWeatherToday,
    refetchRegionClusterStat,
    refetchDrEventPowerMeterDemand,
  }), [
    // 데이터 변경 시에만 재생성
    defaultCblModuleName,
    defaultCblModuleVersion,
    jejuSiData,
    seoguipoSiData,
    drResourceData,
    stationSysIdList,
    predictionCblData,
    liveCblData,
    powerMeterDemandData,
    livePowerMeterDemandData,
    drHistoryLatestData,
    drHistoryRangeData,
    kpxSmpRangeData,
    weatherForecastData,
    weatherTodayData,
    regionClusterStatData,
    drEventPowerMeterDemandData,
    // 로딩 상태 변경 시에만 재생성
    isLoading,
    jejuSiLoading,
    seoguipoSiLoading,
    drResourceLoading,
    predictionCblLoading,
    liveCblLoading,
    powerMeterDemandLoading,
    livePowerMeterDemandLoading,
    drHistoryLatestLoading,
    drHistoryRangeLoading,
    kpxSmpRangeLoading,
    weatherForecastLoading,
    weatherTodayLoading,
    regionClusterStatLoading,
    drEventPowerMeterDemandLoading,
    // 에러 상태 변경 시에만 재생성
    hasError,
    jejuSiError,
    seoguipoSiError,
    drResourceError,
    predictionCblError,
    liveCblError,
    powerMeterDemandError,
    livePowerMeterDemandError,
    drHistoryLatestError,
    drHistoryRangeError,
    kpxSmpRangeError,
    weatherForecastError,
    weatherTodayError,
    regionClusterStatError,
    drEventPowerMeterDemandError,
    // refetchAll은 안정화되어 있으므로 포함
    refetchAll,
    // hasEupmyeondong도 의존성에 추가
    hasEupmyeondong,
    // 다른 refetch 함수들은 안정적이므로 제외 가능하지만, 안전을 위해 포함
    refetchJejuSi,
    refetchSeoguipoSi,
    refetchDrResource,
    refetchPredictionCbl,
    refetchLiveCbl,
    refetchPowerMeterDemand,
    refetchLivePowerMeterDemand,
    refetchDrHistoryLatest,
    refetchDrHistoryRange,
    refetchKpxSmpRange,
    refetchWeatherForecast,
    refetchWeatherToday,
    refetchRegionClusterStat,
    refetchDrEventPowerMeterDemand,
  ]);
};

export default useDashboardRealtimeData;
